/**
 * @file urlUtils.ts
 * @description Utility functions for URL normalization and validation.
 */

/**
 * Normalizes a URL by adding protocol if missing and removing trailing slashes.
 * @param url - The URL string to normalize
 * @returns The normalized URL, or empty string if input is empty
 */
export const normalizeUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }

  let normalized = trimmed;

  // Add protocol if missing
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }

  // Remove trailing slash
  normalized = normalized.replace(/\/$/, '');

  return normalized;
};

/**
 * Validates and normalizes a URL.
 * @param url - The URL string to validate and normalize
 * @returns Object with validation result, normalized URL, and optional error message
 */
export const validateUrl = (
  url: string,
): { isValid: boolean; normalized?: string; error?: string } => {
  if (!url || typeof url !== 'string') {
    return { isValid: true }; // Empty URL is allowed (optional field)
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return { isValid: true }; // Empty URL is allowed
  }

  try {
    const normalized = normalizeUrl(trimmed);
    // Try to create a URL object to validate
    new URL(normalized);
    return { isValid: true, normalized };
  } catch {
    return {
      isValid: false,
      error: "L'URL indicato non risulta raggiungibile. Controlla che sia corretto o copialo dalla barra del browser.",
    };
  }
};
